import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  CheckCircleOutlined,
  CreditCardOutlined,
  LoginOutlined,
  SafetyOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import { useAuth } from "../context/AuthContext";
import "./styles/StormOps.css";

const API_BASE = process.env.REACT_APP_API_URL || "https://api.fans.nextremedy.net";

function alertLabel(preview) {
  if (!preview) return "Official alert preview";
  if (preview.alertCount === 0) return "Standby window";
  return `${preview.alertCount} active official alert${preview.alertCount === 1 ? "" : "s"}`;
}

function previewPayload({ zip, serviceType, result }) {
  return {
    zip,
    serviceType,
    preview: result.preview,
    sourceArtifacts: result.sourceArtifacts,
    providerArtifacts: result.providerArtifacts,
    selectedDirection: result.preview?.recommendedDirection || "",
  };
}

export default function StormOpsLandingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [zip, setZip] = useState("");
  const [serviceType, setServiceType] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const runPreview = async (event) => {
    event.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE}/api/stormops/preview`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ zip, serviceType }),
      });
      const payload = await response.json();

      if (!response.ok || !payload.success) {
        throw new Error(payload.error || "Preview failed");
      }

      const draft = previewPayload({ zip, serviceType, result: payload.data });
      sessionStorage.setItem("stormops.previewDraft", JSON.stringify(draft));
      setPreview(draft);
    } catch (err) {
      setError(err.message || "Unable to create preview");
    } finally {
      setLoading(false);
    }
  };

  const continueToWorkspace = () => {
    navigate(user ? "/campaigns" : "/signup");
  };

  const location = preview?.preview?.location;
  const alerts = preview?.preview?.alerts || [];

  return (
    <main className="storm-shell">
      <header className="storm-nav">
        <div className="storm-brand">
          <span className="storm-brand-mark">S</span>
          <span>StormOps Dispatch</span>
        </div>
        <nav className="storm-nav-actions" aria-label="Account">
          {user ? (
            <Link className="storm-icon-link" to="/campaigns">
              <SafetyOutlined />
              Workspace
            </Link>
          ) : (
            <>
              <Link className="storm-text-link" to="/login">Log in</Link>
              <Link className="storm-icon-link" to="/signup">
                <LoginOutlined />
                Start
              </Link>
            </>
          )}
        </nav>
      </header>

      <section className="storm-workbench">
        <div className="storm-input-zone">
          <p className="storm-kicker">Official-alert-backed customer response</p>
          <h1>Turn a storm ZIP into a customer-ready service update.</h1>
          <p className="storm-lede">
            StormOps reads current National Weather Service alerts, ranks the service risk, and prepares the paid response run around the same source artifacts.
          </p>

          <form className="storm-form" onSubmit={runPreview}>
            <label>
              ZIP code
              <input
                value={zip}
                onChange={(event) => setZip(event.target.value)}
                inputMode="numeric"
                maxLength="5"
                placeholder="78701"
                required
              />
            </label>
            <label>
              Service type
              <input
                value={serviceType}
                onChange={(event) => setServiceType(event.target.value)}
                placeholder="HVAC repair"
                required
              />
            </label>
            <button className="storm-primary" type="submit" disabled={loading}>
              <ThunderboltOutlined />
              {loading ? "Checking alerts" : "Build Preview"}
            </button>
          </form>

          {error && <div className="storm-alert storm-alert-error">{error}</div>}

          <div className="storm-commercial-row">
            <span><CreditCardOutlined /> Paid production run: GBP 49</span>
            <span><CheckCircleOutlined /> No extra processing click after checkout</span>
          </div>
        </div>

        <div className="storm-preview-zone" aria-live="polite">
          {preview ? (
            <>
              <div className="storm-preview-heading">
                <span className={`storm-status storm-status-${preview.preview?.risk?.riskLevel || "standby"}`}>
                  {alertLabel(preview.preview)}
                </span>
                <span>{location ? `${location.city}, ${location.state}` : preview.zip}</span>
              </div>
              <h2>{preview.preview?.risk?.label}</h2>
              <p>{preview.preview?.recommendedDirection}</p>

              <div className="storm-metrics">
                <div>
                  <strong>{preview.preview?.risk?.score}</strong>
                  <span>Risk score</span>
                </div>
                <div>
                  <strong>{preview.preview?.alertCount}</strong>
                  <span>NWS alerts</span>
                </div>
                <div>
                  <strong>{preview.serviceType}</strong>
                  <span>Service</span>
                </div>
              </div>

              <div className="storm-source-line">{preview.preview?.sourceLabel}</div>

              <div className="storm-alert-list">
                {alerts.length > 0 ? alerts.slice(0, 3).map((alert) => (
                  <article key={alert.id || alert.headline}>
                    <span>{alert.severity}</span>
                    <h3>{alert.event}</h3>
                    <p>{alert.headline}</p>
                  </article>
                )) : (
                  <article>
                    <span>Standby</span>
                    <h3>No active alert currently reported</h3>
                    <p>The production run will still create a customer availability page and monitoring history.</p>
                  </article>
                )}
              </div>

              <button className="storm-primary storm-wide" type="button" onClick={continueToWorkspace}>
                <CreditCardOutlined />
                {user ? "Create Paid Run" : "Create Account To Activate"}
              </button>
            </>
          ) : (
            <div className="storm-empty-preview">
              <SafetyOutlined />
              <h2>Preview appears here</h2>
              <p>Enter a ZIP code and service type to pull live official alert data.</p>
            </div>
          )}
        </div>
      </section>
    </main>
  );
}
